/**
 * Board invariants — constants and pure helpers shared across the store
 * sub-modules. Pulled out of `stores/gvgPlannerStore.ts` so unit tests under
 * `node --test` can import them without dragging in Pinia / Apollo / Vue
 * (the store itself is too heavy to load in a plain Node test runner).
 */
import { PlayerRole } from '@/types/weapon'

import type { GroupId, GroupPosition, PlayerNameMode, PlayerTag, Room, RoomAccent, RoomId, TagColor, ViewMode } from '../types/index.ts'
import {
  GroupPosition as GroupPositionValue,
  PlayerNameMode as PlayerNameModeValue,
  RoomAccent as RoomAccentValue,
  TagColor as TagColorValue,
  ViewMode as ViewModeValue,
} from '../types/index.ts'

export const POOL_ROOM_ID: RoomId = 'pool'
export const POOL_SIGNED_GROUP_ID: GroupId = 'P2'
export const POOL_OTHER_GROUP_ID: GroupId = 'P3'
export const STATIC_POOL_GROUP_IDS: ReadonlySet<GroupId> = new Set<GroupId>([
  POOL_SIGNED_GROUP_ID,
  POOL_OTHER_GROUP_ID,
])
export const POOL_DEFAULT_GROUP_ID: GroupId = POOL_OTHER_GROUP_ID

export const MIN_MAX_PLAYERS = 1
export const MAX_MAX_PLAYERS = 10

export const MAX_TAGS_PER_PLAYER = 10

/**
 * Frontend mirror of `MAX_PERSONAL_BUILDS_PER_PLAYER` in
 * `apps/backend/shared/common/src/invariants.rs`.
 */
export const MAX_SAVED_BUILDS = 30

/** Raid snapshots retain at most six builds, regardless of personal library size. */
export const MAX_RAID_SNAPSHOT_BUILDS = 6

export class UnsupportedActionError extends Error {
  readonly action: string
  constructor(action: string) {
    super(`Store action "${action}" is not backed by a server mutation yet.`)
    this.name = 'UnsupportedActionError'
    this.action = action
  }
}

export function clampMax(value: number): number {
  if (!Number.isFinite(value)) return MIN_MAX_PLAYERS
  const rounded = Math.round(value)
  return Math.min(MAX_MAX_PLAYERS, Math.max(MIN_MAX_PLAYERS, rounded))
}

export function isViewMode(raw: unknown): raw is ViewMode {
  return raw === ViewModeValue.Detail || raw === ViewModeValue.Compact
}

export function isPlayerNameMode(raw: unknown): raw is PlayerNameMode {
  return raw === PlayerNameModeValue.GuildNickname
    || raw === PlayerNameModeValue.PlayerNickname
    || raw === PlayerNameModeValue.PlayerAccountName
}

export function pickNextRoomAccent(existingRooms: ReadonlyArray<Room>): RoomAccent {
  const cycle: ReadonlyArray<RoomAccent> = [
    RoomAccentValue.Red,
    RoomAccentValue.Blue,
    RoomAccentValue.Green,
    RoomAccentValue.Purple,
  ]
  const nonPoolCount = existingRooms.filter((r) => r.id !== POOL_ROOM_ID).length
  return cycle[nonPoolCount % cycle.length] ?? RoomAccentValue.Red
}

export function parseAccent(s: string): RoomAccent {
  switch (s) {
    case 'blue':
      return RoomAccentValue.Blue
    case 'green':
      return RoomAccentValue.Green
    case 'purple':
      return RoomAccentValue.Purple
    default:
      return RoomAccentValue.Red
  }
}

export function parsePosition(s: string): GroupPosition {
  switch (s) {
    case 'mid':
      return GroupPositionValue.Mid
    case 'bot':
      return GroupPositionValue.Bot
    default:
      return GroupPositionValue.Top
  }
}

export function parseTag(t: {
  readonly id: number
  readonly guildId: number
  readonly name: string
  readonly seedKey: string | null
  readonly color: string
}): PlayerTag {
  return {
    id: String(t.id),
    guildId: String(t.guildId),
    name: t.name,
    seedKey: t.seedKey,
    color: parseTagColor(t.color),
  }
}

export function parseTagColor(s: string): TagColor {
  switch (s) {
    case 'Red':
      return TagColorValue.Red
    case 'Blue':
      return TagColorValue.Blue
    case 'Green':
      return TagColorValue.Green
    case 'Purple':
      return TagColorValue.Purple
    case 'Amber':
      return TagColorValue.Amber
    case 'Rose':
      return TagColorValue.Rose
    case 'Cyan':
      return TagColorValue.Cyan
    case 'Slate':
      return TagColorValue.Slate
    case 'Stone':
      return TagColorValue.Stone
    case 'Neutral':
      return TagColorValue.Neutral
    default:
      return TagColorValue.Neutral
  }
}

export function parseRole(s: string): PlayerRole {
  switch (s) {
    case 'TANK':
      return PlayerRole.Tank
    case 'HYBRID':
      return PlayerRole.Hybrid
    case 'HEAL':
      return PlayerRole.Heal
    default:
      return PlayerRole.Dps
  }
}
